import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { FileText, Link as LinkIcon, Paperclip, Trash2, Upload } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

export type AttachmentEntity = 'OPPORTUNITY' | 'ACCOUNT' | 'LEAD' | 'CONTACT' | 'ACTIVITY';

interface Attachment {
  id: string;
  entityType: string;
  entityId: string;
  kind: 'FILE' | 'LINK';
  title: string;
  description: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  url: string | null;
  ogImageUrl: string | null;
  uploadedBy: string | null;
  createdAt: string | null;
  /** 100.5 — vendor authenticity verdict; null until PATCHed. */
  authenticityStatus:
    | 'authentic'
    | 'flagged'
    | 'requires_review'
    | 'rejected'
    | null;
}

const VERDICT_BADGE: Record<string, string> = {
  authentic: 'bg-emerald-900/40 text-emerald-200',
  flagged: 'bg-red-900/40 text-red-200',
  requires_review: 'bg-amber-900/40 text-amber-200',
  rejected: 'bg-red-900/60 text-red-100',
};

interface Props {
  entityType: AttachmentEntity;
  entityId: string;
  /** Optional title override; defaults to a translated "Attachments" header. */
  title?: string;
  /** Hide the panel until at least one attachment exists. */
  collapseWhenEmpty?: boolean;
}

const HUMAN_BYTES = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Epic 88, Story 88.6 — drag-drop file uploads + paste-a-link panel.
 * Used on the detail page of every attachment-bearing entity
 * (opportunity / account / lead / contact / activity).
 */
export function AttachmentPanel({
  entityType,
  entityId,
  title,
  collapseWhenEmpty = false,
}: Props) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function load() {
    setLoading(true);
    apiClient
      .get<Attachment[]>('/attachments', {
        params: { entity: entityType, entityId },
      })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }

  useEffect(load, [entityType, entityId]);

  async function uploadFiles(files: FileList | File[]) {
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entityType', entityType);
        fd.append('entityId', entityId);
        await apiClient.post('/attachments', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      load();
    } catch (err) {
      const status = isAxiosError(err) ? err.response?.status : null;
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(
        status === 413
          ? t('attachments.error_quota')
          : detail ?? t('attachments.error_upload'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function addLink() {
    if (!linkUrl.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post('/attachments/links', {
        entityType,
        entityId,
        url: linkUrl.trim(),
        title: linkTitle.trim() || linkUrl.trim(),
      });
      setLinkUrl('');
      setLinkTitle('');
      load();
    } catch {
      setError(t('attachments.error_link'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm(t('attachments.confirm_delete'))) return;
    try {
      await apiClient.delete(`/attachments/${id}`);
      load();
    } catch {
      setError(t('attachments.error_delete'));
    }
  }

  function open(item: Attachment) {
    if (item.kind === 'LINK' && item.url) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    } else {
      window.open(`/api/v1/attachments/${item.id}/content`, '_blank');
    }
  }

  if (collapseWhenEmpty && !loading && items.length === 0) {
    return (
      <div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip className="mr-2 h-3.5 w-3.5" />
          {t('attachments.add')}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => e.target.files && uploadFiles(e.target.files)}
        />
      </div>
    );
  }

  return (
    <section className="rounded-lg border bg-card">
      <header className="flex items-center justify-between border-b px-4 py-2">
        <h3 className="text-sm font-medium">{title ?? t('attachments.title')}</h3>
        <span className="text-xs text-muted-foreground">{items.length}</span>
      </header>
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files);
        }}
        className={
          'flex items-center justify-between gap-2 border-b px-4 py-3 text-sm transition ' +
          (drag ? 'bg-primary/5' : '')
        }
      >
        <div className="text-muted-foreground">
          <Upload className="inline h-3.5 w-3.5 mr-1" />
          {t('attachments.drop_or')}
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          {t('attachments.choose_file')}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => e.target.files && uploadFiles(e.target.files)}
        />
      </div>
      <div className="border-b px-4 py-3">
        <label className="text-xs font-medium text-muted-foreground">
          {t('attachments.add_link')}
        </label>
        <div className="mt-1 flex gap-2">
          <Input
            value={linkUrl}
            placeholder="https://…"
            onChange={(e) => setLinkUrl(e.target.value)}
          />
          <Input
            value={linkTitle}
            placeholder={t('attachments.link_title_placeholder')}
            onChange={(e) => setLinkTitle(e.target.value)}
          />
          <Button size="sm" disabled={busy || !linkUrl.trim()} onClick={addLink}>
            {t('attachments.add')}
          </Button>
        </div>
      </div>
      {error && (
        <div className="border-b bg-red-950/40 px-4 py-2 text-xs text-red-300">
          {error}
        </div>
      )}
      <ul className="divide-y">
        {loading && (
          <li className="px-4 py-3 text-sm text-muted-foreground">
            {t('common.loading')}
          </li>
        )}
        {!loading && items.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t('attachments.empty')}
          </li>
        )}
        {items.map((it) => (
          <li
            key={it.id}
            className="flex items-center justify-between px-4 py-2 hover:bg-accent/30"
          >
            <button
              type="button"
              className="flex flex-1 items-center gap-2 text-left"
              onClick={() => open(it)}
            >
              {it.kind === 'FILE' ? (
                <FileText className="h-4 w-4 text-muted-foreground" />
              ) : (
                <LinkIcon className="h-4 w-4 text-muted-foreground" />
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">{it.title}</span>
                  {it.authenticityStatus && (
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                        VERDICT_BADGE[it.authenticityStatus] ??
                          'bg-muted text-muted-foreground',
                      )}
                    >
                      {t(`attachments.verdict_${it.authenticityStatus}`)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {it.kind === 'FILE'
                    ? [it.mimeType, it.sizeBytes ? HUMAN_BYTES(it.sizeBytes) : null]
                        .filter(Boolean)
                        .join(' · ')
                    : it.url}
                </div>
              </div>
            </button>
            <button
              type="button"
              className="ml-2 text-muted-foreground hover:text-destructive"
              onClick={() => remove(it.id)}
              aria-label={t('attachments.delete')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
